import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Landmark, Plus, Loader2, Banknote, HandCoins, BarChart3, Building2 } from "lucide-react";

const SECTORS = ["Énergie", "Agriculture", "Infrastructure", "Mines", "Technologie", "Numérique", "Immobilier", "Santé"];
const empty = { title: "", sector: "Énergie", short_desc: "", description: "", story: "", location: "", funding_goal: 100000, interest_rate: 8, duration_months: 24, risk_level: 3, jobs_impact: 0, energy_impact: "", financing_type: "PPP", partners: "", lat: "", lng: "", image_url: "" };

export default function CadecoBackoffice() {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [recon, setRecon] = useState(null);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/projects").then(({ data }) => setProjects(data));
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/reconciliation").then(({ data }) => setRecon(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/projects", {
        ...form, funding_goal: Number(form.funding_goal), interest_rate: Number(form.interest_rate),
        duration_months: Number(form.duration_months), risk_level: Number(form.risk_level), jobs_impact: Number(form.jobs_impact),
        lat: form.lat === "" ? null : Number(form.lat), lng: form.lng === "" ? null : Number(form.lng),
        partners: form.partners ? form.partners.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast.success("Projet créé (brouillon). Studeur doit le publier.");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const changeStatus = async (id, status) => {
    await api.post(`/projects/${id}/status?status=${status}`); toast.success("Statut mis à jour"); load();
  };
  const disburse = async (id) => {
    try { await api.post(`/projects/${id}/disburse`); toast.success("Fonds décaissés au porteur"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const repay = async (id) => {
    try { const { data } = await api.post(`/projects/${id}/repay`); toast.success(`${data.repayments} remboursement(s) effectué(s)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const statusColor = { draft: "bg-slate-200 text-slate-700", pending_review: "bg-amber-100 text-amber-700", active: "bg-green-100 text-green-700", funded: "bg-blue-100 text-blue-700", disbursed: "bg-purple-100 text-purple-700", closed: "bg-slate-200 text-slate-500" };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-congo-bluelight flex items-center justify-center"><Landmark className="h-6 w-6 text-congo-blue" /></div>
          <div><h1 className="font-head text-2xl font-bold text-slate-900">Back-office CADECO</h1>
            <p className="text-slate-500 text-sm">Gestion des flux financiers, projets et décaissements</p></div>
        </div>

        {stats && recon && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-white border rounded-xl p-4"><Banknote className="h-5 w-5 text-congo-blue" /><p className="text-xs text-slate-500 mt-2">Fonds collectés</p><p className="font-head text-xl font-bold">{money(recon.total_collected)}</p></div>
            <div className="bg-white border rounded-xl p-4"><HandCoins className="h-5 w-5 text-green-600" /><p className="text-xs text-slate-500 mt-2">Solde séquestre</p><p className="font-head text-xl font-bold">{money(recon.escrow_balance)}</p></div>
            <div className="bg-white border rounded-xl p-4"><BarChart3 className="h-5 w-5 text-congo-blue" /><p className="text-xs text-slate-500 mt-2">Investissements</p><p className="font-head text-xl font-bold">{recon.confirmed_investments}</p></div>
            <div className="bg-white border rounded-xl p-4"><Building2 className="h-5 w-5 text-purple-600" /><p className="text-xs text-slate-500 mt-2">Projets financés</p><p className="font-head text-xl font-bold">{recon.funded_projects}</p></div>
          </div>
        )}

        <Tabs defaultValue="projects" className="mt-8">
          <TabsList className="bg-slate-100"><TabsTrigger value="projects" data-testid="tab-projects">Projets</TabsTrigger><TabsTrigger value="recon" data-testid="tab-recon">Réconciliation</TabsTrigger></TabsList>

          <TabsContent value="projects" className="pt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-head text-lg font-semibold">Portefeuille de projets</h2>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button className="rounded-full bg-congo-blue" data-testid="new-project-btn"><Plus className="h-4 w-4 mr-1" /> Nouveau projet</Button></DialogTrigger>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-auto" data-testid="project-dialog">
                  <DialogHeader><DialogTitle className="font-head">Créer un projet</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Titre</Label><Input value={form.title} onChange={set("title")} data-testid="pf-title" className="mt-1" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Secteur</Label>
                        <Select value={form.sector} onValueChange={set("sector")}><SelectTrigger className="mt-1" data-testid="pf-sector"><SelectValue /></SelectTrigger>
                          <SelectContent>{SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                      <div><Label>Localisation</Label><Input value={form.location} onChange={set("location")} data-testid="pf-location" className="mt-1" /></div>
                    </div>
                    <div><Label>Description courte</Label><Input value={form.short_desc} onChange={set("short_desc")} data-testid="pf-short" className="mt-1" /></div>
                    <div><Label>Description complète</Label><Textarea value={form.description} onChange={set("description")} data-testid="pf-desc" className="mt-1" /></div>
                    <div><Label>Storytelling</Label><Textarea value={form.story} onChange={set("story")} className="mt-1" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Objectif ($)</Label><Input type="number" value={form.funding_goal} onChange={set("funding_goal")} data-testid="pf-goal" className="mt-1" /></div>
                      <div><Label>Rendement (%/an)</Label><Input type="number" value={form.interest_rate} onChange={set("interest_rate")} className="mt-1" /></div>
                      <div><Label>Durée (mois)</Label><Input type="number" value={form.duration_months} onChange={set("duration_months")} className="mt-1" /></div>
                      <div><Label>Risque (1-5)</Label><Input type="number" min={1} max={5} value={form.risk_level} onChange={set("risk_level")} className="mt-1" /></div>
                      <div><Label>Emplois créés</Label><Input type="number" value={form.jobs_impact} onChange={set("jobs_impact")} className="mt-1" /></div>
                      <div><Label>Énergie</Label><Input value={form.energy_impact} onChange={set("energy_impact")} placeholder="ex: 50 MW" className="mt-1" /></div>
                    </div>
                    <div><Label>URL image</Label><Input value={form.image_url} onChange={set("image_url")} placeholder="https://…" className="mt-1" /></div>
                    <div><Label>Nature du montage</Label><Input value={form.financing_type} onChange={set("financing_type")} placeholder="PPP, Concession, Obligations, Rachat de parts…" data-testid="pf-financing" className="mt-1" /></div>
                    <div><Label>Partenaires (séparés par des virgules)</Label><Input value={form.partners} onChange={set("partners")} placeholder="CADECO, SCPT, Equity BCDC…" data-testid="pf-partners" className="mt-1" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Latitude</Label><Input type="number" step="0.01" value={form.lat} onChange={set("lat")} placeholder="-4.34" data-testid="pf-lat" className="mt-1" /></div>
                      <div><Label>Longitude</Label><Input type="number" step="0.01" value={form.lng} onChange={set("lng")} placeholder="15.27" data-testid="pf-lng" className="mt-1" /></div>
                    </div>
                    <Button onClick={create} disabled={busy} data-testid="save-project-btn" className="w-full rounded-full bg-congo-blue">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer le projet"}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-3" data-testid="admin-projects-list">
              {projects.map((p) => (
                <div key={p.id} className="bg-white border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><p className="font-medium truncate">{p.title}</p><Badge className={`border-0 ${statusColor[p.status]}`}>{p.status}</Badge></div>
                    <p className="text-xs text-slate-500 mt-1">{money(p.amount_collected)} / {money(p.funding_goal)} · {p.progress_pct}% · {p.investors_count} investisseurs</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {p.status === "funded" && <Button size="sm" onClick={() => disburse(p.id)} data-testid={`disburse-${p.id}`} className="rounded-full bg-purple-600 hover:bg-purple-700">Décaisser</Button>}
                    {p.status === "disbursed" && <Button size="sm" onClick={() => repay(p.id)} data-testid={`repay-${p.id}`} className="rounded-full bg-green-600 hover:bg-green-700">Rembourser</Button>}
                    {p.status === "active" && <Button size="sm" variant="outline" onClick={() => changeStatus(p.id, "closed")} className="rounded-full">Clôturer</Button>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="recon" className="pt-6">
            {recon && (
              <div className="bg-white border rounded-2xl p-6 max-w-lg">
                <h2 className="font-head text-lg font-semibold">Rapport de réconciliation</h2>
                <p className="text-xs text-slate-400">{new Date(recon.date).toLocaleString("fr-FR")}</p>
                <dl className="mt-4 space-y-3">
                  {[
                    ["Total collecté", money(recon.total_collected)],
                    ["Solde sous séquestre", money(recon.escrow_balance)],
                    ["Total remboursé", money(recon.total_repaid)],
                    ["Projets actifs", recon.active_projects],
                    ["Projets financés", recon.funded_projects],
                    ["Investissements confirmés", recon.confirmed_investments],
                    ["Investissements en attente", recon.pending_investments],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500 text-sm">{k}</dt><dd className="font-semibold">{v}</dd></div>
                  ))}
                </dl>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
